import { compact, groupBy, isNil, mapValues, reduce, some, sum } from 'lodash';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { SpotterTelemetryCapabilities, SubscriptionTagInfo, useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { SubscriptionItem, useSubscriptionsInfo, useSubscriptionsItems } from '@/api/subscriptions';
import { useUserAccount } from '@/api/userAccount';
import {
    useTransactionContext,
    useTransactionContextLineItemQuantity,
} from '@/context/transaction/TransactionContextProvider';
import { UNUSED_LINES_NOTIFICATION_ID, useNotifications } from '@/hooks/useNotifications';
import { pickDefined, showUiElementPerIridiumVar } from '@/utils';

export type LineType = SpotterTelemetryCapabilities;

export type TelemetryLineChangeset = { [spotterId: string]: { iridium?: boolean; cellular?: boolean } };

export type TelemetryLineChangeContext = {
    telemetryLineChanges: TelemetryLineChangeset;
    toggleTelemetryLine: (spotterId: string, lineType: LineType) => void;
    setTelemetryLineChanges: React.Dispatch<React.SetStateAction<TelemetryLineChangeset>>;
    disableAllLines: () => boolean;
    isRetainExtraLines: boolean | undefined;
    setRetainExtraLines: React.Dispatch<React.SetStateAction<boolean | undefined>>;
    numActivatedLines: Record<LineType, number>;
    numDeactivatedLines: Record<LineType, number>;
    numExtraLines: Record<LineType, number>;
    hasUnusedLines: boolean;
    affectedSpotters: Record<LineType, string[]>;
};

export type TelemetryLineChangeContextProviderProps = {
    children: ReactNode;
};

export function findByTelemetryLinePriceLookupKey<I extends { price_lookup_key: string; interval: string }>(
    items: I[] | undefined,
    type: LineType,
    interval?: 'month' | 'year'
) {
    return items?.find(
        (item) => item.price_lookup_key.includes(`${type}_access`) && (!interval || item.interval === interval)
    );
}

function byLineType<R>(fn: (type: LineType) => R) {
    return { iridium: fn('iridium'), cellular: fn('cellular') };
}

export const subscribedLineCounts = (items?: SubscriptionItem[]) =>
    byLineType((type) => findByTelemetryLinePriceLookupKey(items, type)?.quantity ?? 0);

export const enabledLineCounts = (tagsInfos?: SubscriptionTagInfo[]) =>
    byLineType((type) =>
        sum(tagsInfos?.map((tags) => (tags[`should_${type}_be_active`] && tags[`is_${type}_allowed`] ? 1 : 0)))
    );

export const TelemetryLineChangeContextProvider = ({ children }: TelemetryLineChangeContextProviderProps) => {
    const {
        setConcurrentSubscriptionTagBatch,
        isPending,
        mutationStatus: { isIdle: subscriptionUpdateIdle, isSuccess: subscriptionUpdateSuccess } = {},
    } = useTransactionContext('subscription');
    const enableSubscriptionQueries = subscriptionUpdateIdle;

    const { data: subscriptionItems } = useSubscriptionsItems({ config: { enabled: enableSubscriptionQueries } });
    const { data: accountData } = useUserAccount();
    const accountIridiumVar = accountData?.data?.user?.accountIridiumVar;
    const varSupportsDisabling = showUiElementPerIridiumVar(
        accountIridiumVar,
        'varSupportsDisablingSatelliteTelemetry',
        'every'
    );

    const [, setIridiumLineQty] = useTransactionContextLineItemQuantity(
        findByTelemetryLinePriceLookupKey(subscriptionItems?.data, 'iridium', 'month')?.price_id
    );
    const [, setCellularLineQty] = useTransactionContextLineItemQuantity(
        findByTelemetryLinePriceLookupKey(subscriptionItems?.data, 'cellular', 'month')?.price_id
    );
    const { data: subscriptionInfo } = useSubscriptionsInfo({ config: { enabled: enableSubscriptionQueries } });
    const { data: subscriptionTagsInfo } = useSubscriptionTagsInfo({ config: { enabled: enableSubscriptionQueries } });
    const { undismissNotification } = useNotifications();
    const [telemetryLineChanges, setTelemetryLineChanges] = useState<TelemetryLineChangeset>({});
    const [isRetainExtraLines, setRetainExtraLines] = useState<boolean>();

    /**
     * Memoize tabulations derived from subscriptionInfo and subscriptionTagsInfo
     */
    const { subscribedQtys, numCurrentlyUsed, tagIdBySpotId } = useMemo(() => {
        return {
            subscribedQtys: subscribedLineCounts(subscriptionInfo?.data?.[0]?.items),
            numCurrentlyUsed: enabledLineCounts(subscriptionTagsInfo?.data),
            tagIdBySpotId: mapValues(
                groupBy(subscriptionTagsInfo?.data, 'spotter_id'),
                (tagInfos) => tagInfos[0].subscription_tag_id
            ),
        };
    }, [subscriptionInfo?.data, subscriptionTagsInfo?.data]);

    /**
     * Memoize tabulations derived from telemetryLineChanges
     */
    const { numActivatedLines, numDeactivatedLines, affectedSpotters } = useMemo(() => {
        return reduce(
            Object.entries(telemetryLineChanges),
            ({ numActivatedLines, numDeactivatedLines, affectedSpotters }, [spotterId, spotterLineChanges]) => ({
                numActivatedLines: byLineType((type) => numActivatedLines[type] + (spotterLineChanges[type] ? 1 : 0)),
                numDeactivatedLines: byLineType(
                    (type) => numDeactivatedLines[type] + (spotterLineChanges[type] === false ? 1 : 0)
                ),
                affectedSpotters: byLineType((type) =>
                    !isNil(spotterLineChanges[type]) ? [spotterId, ...affectedSpotters[type]] : affectedSpotters[type]
                ),
            }),
            {
                affectedSpotters: { iridium: [] as string[], cellular: [] as string[] },
                numActivatedLines: { iridium: 0, cellular: 0 },
                numDeactivatedLines: { iridium: 0, cellular: 0 },
            }
        );
    }, [telemetryLineChanges]);

    /**
     * Memoize tabulations derived from the previous two memos
     */
    const { targetQtys, numExtraLines, hasUnusedLines, subscriptionTagUpdateBatch } = useMemo(() => {
        const numExtraLines = byLineType((type) =>
            Math.max(0, subscribedQtys[type] - numCurrentlyUsed[type] - numActivatedLines[type])
        );
        const targetQtys = byLineType((type) =>
            Math.max(
                isRetainExtraLines ? subscribedQtys[type] : 0,
                numCurrentlyUsed[type] + numActivatedLines[type] - numDeactivatedLines[type]
            )
        );

        const hasUnusedLines = some(
            Object.values(
                byLineType(
                    (type) =>
                        numCurrentlyUsed[type] + numActivatedLines[type] - numDeactivatedLines[type] <
                        subscribedQtys[type]
                )
            )
        );

        const subscriptionTagUpdateBatch = Object.fromEntries(
            compact(
                Object.entries(telemetryLineChanges).map(([spotterId, { iridium, cellular }]) => {
                    const tags = pickDefined({
                        should_iridium_be_active: iridium,
                        should_cellular_be_active: cellular,
                    });
                    if (!Object.keys(tags).length) return null;
                    return [tagIdBySpotId[spotterId], tags];
                })
            )
        );

        return { numExtraLines, targetQtys, hasUnusedLines, subscriptionTagUpdateBatch };
    }, [
        isRetainExtraLines,
        numActivatedLines,
        numCurrentlyUsed,
        numDeactivatedLines,
        subscribedQtys,
        tagIdBySpotId,
        telemetryLineChanges,
    ]);

    /**
     * Effect: Default isRetainExtraLines to true only if we came in with more lines than we're using
     */
    useEffect(() => {
        if (isNil(isRetainExtraLines) && subscriptionInfo && subscriptionTagsInfo) {
            setRetainExtraLines((value) => {
                if (isNil(value)) {
                    // double checking it's still undefined in case we're racing with someone setting it explicitly
                    return (
                        subscribedQtys.iridium > numCurrentlyUsed.iridium ||
                        subscribedQtys.cellular > numCurrentlyUsed.cellular
                    );
                } else {
                    return value;
                }
            });
        }
    }, [subscribedQtys, isRetainExtraLines, subscriptionInfo, subscriptionTagsInfo, numCurrentlyUsed]);

    /**
     * Effect: Propagate quantity changes into the pending transaction
     */
    const anyAffectedSpotters = some(affectedSpotters, (l) => !!l.length);
    useEffect(() => {
        if (!isPending) {
            setIridiumLineQty(targetQtys.iridium);
            setCellularLineQty(targetQtys.cellular);
            setConcurrentSubscriptionTagBatch?.(subscriptionTagUpdateBatch);
        }
    }, [
        anyAffectedSpotters,
        setConcurrentSubscriptionTagBatch,
        setCellularLineQty,
        setIridiumLineQty,
        targetQtys.cellular,
        targetQtys.iridium,
        isPending,
        subscriptionTagUpdateBatch,
    ]);

    /**
     * Effect: On successful mutation, if they stopped retaining unused lines, clear the dismissal of the unused lines alert so it can
     * come back next time.
     */
    useEffect(() => {
        if (subscriptionUpdateSuccess && !isRetainExtraLines) {
            undismissNotification(UNUSED_LINES_NOTIFICATION_ID);
        }
    }, [subscriptionUpdateSuccess, isRetainExtraLines, undismissNotification]);

    const toggleTelemetryLine = useCallback(
        (spotterId: string, lineType: LineType) => {
            const currentTags = subscriptionTagsInfo?.data?.find(({ spotter_id }) => spotterId === spotter_id);
            if (!currentTags) return;

            setTelemetryLineChanges((value) => {
                const currentChange = value[spotterId]?.[lineType];
                const newValue = isNil(currentChange) ? !currentTags[`should_${lineType}_be_active`] : undefined;

                return {
                    ...value,
                    [spotterId]: { ...value[spotterId], [lineType]: newValue },
                };
            });
        },
        [subscriptionTagsInfo?.data]
    );

    const disableAllLines = useCallback(() => {
        if (!subscriptionTagsInfo?.data) {
            return false;
        }
        setTelemetryLineChanges(
            Object.fromEntries(
                subscriptionTagsInfo?.data.map(
                    ({ spotter_id, should_cellular_be_active, should_iridium_be_active }) => [
                        spotter_id,
                        {
                            iridium: varSupportsDisabling && should_iridium_be_active ? false : undefined,
                            cellular: should_cellular_be_active ? false : undefined,
                        },
                    ]
                )
            )
        );
        return true;
    }, [subscriptionTagsInfo?.data, varSupportsDisabling]);

    const contextValue: TelemetryLineChangeContext = useMemo(
        () => ({
            telemetryLineChanges,
            setTelemetryLineChanges,
            toggleTelemetryLine,
            disableAllLines,
            isRetainExtraLines,
            setRetainExtraLines,
            numExtraLines,
            hasUnusedLines,
            numActivatedLines,
            numDeactivatedLines,
            affectedSpotters,
        }),
        [
            telemetryLineChanges,
            toggleTelemetryLine,
            disableAllLines,
            isRetainExtraLines,
            numExtraLines,
            hasUnusedLines,
            numActivatedLines,
            numDeactivatedLines,
            affectedSpotters,
        ]
    );

    return <TelemetryLineChangeContext.Provider value={contextValue}>{children}</TelemetryLineChangeContext.Provider>;
};

const TelemetryLineChangeContext = createContext({} as TelemetryLineChangeContext);

export const useTelemetryLineChanges = () => useContext(TelemetryLineChangeContext);
